import { useRef } from "react";
import { usePlosTranslations } from "../../contexts/PlosTranslationsContext";
import ChevronDoubleLeft from "../../img/icons/chevron-double-left.svg?react";
import ChevronDoubleRight from "../../img/icons/chevron-double-right.svg?react";
import ChevronLeft from "../../img/icons/chevron-left.svg?react";
import ChevronRight from "../../img/icons/chevron-right.svg?react";
import LeftArrow from "../../img/icons/left-arrow.svg?react";
import RightArrow from "../../img/icons/right-arrow.svg?react";
import { rtlRotate180 } from "../../styles/utils.css";
import { paginatorWrapper } from "./paginator.css";
import PaginatorButton from "./PaginatorButton";
import { textButtonIcon } from "./PaginatorButton/paginatorButton.css";
import { PaginatorProps } from "./types";

export const paginate = <T,>(data: T[], page: number, pageSize: number) => {
  const start = (page - 1) * pageSize;
  return {
    data: data.slice(start, start + pageSize),
    totalPages: Math.ceil(data.length / pageSize),
  };
};

const Paginator = ({
  page,
  setPage,
  totalPages,
  variant = "chevrons",
  isDisabled,
}: PaginatorProps) => {
  const { paginator } = usePlosTranslations();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Scroll to the top of the container on page change
  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    wrapperRef.current?.parentElement?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (variant === "text") {
    return (
      <div className={paginatorWrapper} ref={wrapperRef}>
        <PaginatorButton
          onPress={() => goToPage(page - 1)}
          label={paginator.previous}
          disabled={isDisabled || page === 1}
          variant="text"
        >
          <LeftArrow className={textButtonIcon} />
          {paginator.previous}
        </PaginatorButton>
        <div role="status" aria-live="polite">
          {paginator.pageOfTotal(page, totalPages)}
        </div>
        <PaginatorButton
          onPress={() => goToPage(page + 1)}
          label={paginator.next}
          disabled={isDisabled || page === totalPages}
          variant="text"
        >
          {paginator.next} <RightArrow className={textButtonIcon} />
        </PaginatorButton>
      </div>
    );
  }

  return (
    <div className={paginatorWrapper} ref={wrapperRef}>
      <PaginatorButton
        onPress={() => goToPage(1)}
        label={paginator.first}
        disabled={isDisabled || page === 1}
        variant="double"
      >
        <ChevronDoubleLeft className={rtlRotate180} />
      </PaginatorButton>
      <PaginatorButton
        onPress={() => goToPage(page - 1)}
        label={paginator.previous}
        disabled={isDisabled || page === 1}
      >
        <ChevronLeft className={rtlRotate180} />
      </PaginatorButton>
      <div role="status" aria-live="polite">
        {paginator.pageOfTotal(page, totalPages)}
      </div>
      <PaginatorButton
        onPress={() => goToPage(page + 1)}
        label={paginator.next}
        disabled={isDisabled || page === totalPages}
      >
        <ChevronRight className={rtlRotate180} />
      </PaginatorButton>
      <PaginatorButton
        onPress={() => goToPage(totalPages)}
        label={paginator.last}
        disabled={isDisabled || page === totalPages}
        variant="double"
      >
        <ChevronDoubleRight className={rtlRotate180} />
      </PaginatorButton>
    </div>
  );
};

export default Paginator;
